import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemPreviewModal } from './ItemPreviewModal';
import { MatchModeConfig } from './matchConfig';
import { RoundState } from './useMatchSession';

/*
  Palette lifted from ImageQuiz so a checked row looks the same as a checked
  answer in the daily rounds.
*/
const RIGHT = 'border-[#20792B] bg-[#E8FDEA] dark:bg-[#1D351F]';
const WRONG = 'border-[#aa0817] bg-[#fde8ea] dark:bg-[#361D1F]';
const EMPTY =
  'border-dashed border-gray-300 dark:border-slate-600 bg-gray-50/60 dark:bg-white/[0.03]';
const HOVER = 'border-[#1a76d2] bg-blue-50 dark:bg-blue-900/30';

/**
 * The drop zone and the flag tile are deliberately the same box, so a flag
 * lands in a frame of exactly its own size.
 */
const BOX = 'w-[7rem] md:w-40 max-h-full shrink-0';

/** The board claims this much of the viewport; everything else stays compact. */
const BOARD_HEIGHT = 'h-[60dvh] min-h-[15rem] md:min-h-[22rem]';

/** Pointer travel before a press counts as a drag rather than a tap. */
const DRAG_THRESHOLD_PX = 8;

type DragState = {
  flag: number;
  x: number;
  y: number;
  width: number;
  height: number;
  moved: boolean;
};

type DropTarget = number | 'tray' | null;

export const MatchBoard = ({
  config,
  round,
  onPlace,
  onClear,
  onAdvance,
}: {
  config: MatchModeConfig;
  round: RoundState;
  onPlace: (flag: number, slot: number) => void;
  onClear: (slot: number) => void;
  onAdvance: () => void;
}) => {
  const { t } = useTranslation();
  const i18nBase = config.i18nBase;
  const { cities, trayOrder, placements, phase, results } = round;

  const [drag, setDrag] = useState<DragState | null>(null);
  const [preview, setPreview] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [hover, setHover] = useState<DropTarget>(null);

  const slotRefs = useRef<(HTMLButtonElement | null)[]>([]);
  /** Every flag cell counts as "back to the flags" when dropped on. */
  const trayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const startRef = useRef({ x: 0, y: 0 });
  // A drag ends with a click event too; this stops that click opening the preview.
  const draggedRef = useRef(false);

  const isPlacing = phase === 'placing';
  const isResolved = phase === 'resolved';

  const locate = useCallback((x: number, y: number): DropTarget => {
    const inside = (element: Element | null) => {
      const rect = element?.getBoundingClientRect();
      return (
        !!rect &&
        x >= rect.left &&
        x <= rect.right &&
        y >= rect.top &&
        y <= rect.bottom
      );
    };

    const slot = slotRefs.current.findIndex((element) => inside(element));
    if (slot >= 0) return slot;
    return trayRefs.current.some((element) => inside(element)) ? 'tray' : null;
  }, []);

  const returnToTray = useCallback(
    (flag: number) => {
      const slot = placements.indexOf(flag);
      if (slot >= 0) onClear(slot);
    },
    [onClear, placements],
  );

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>, flag: number) => {
      if (!isPlacing) return;
      const rect = event.currentTarget.getBoundingClientRect();
      startRef.current = { x: event.clientX, y: event.clientY };
      draggedRef.current = false;
      event.currentTarget.setPointerCapture(event.pointerId);
      setDrag({
        flag,
        x: event.clientX,
        y: event.clientY,
        width: rect.width,
        height: rect.height,
        moved: false,
      });
    },
    [isPlacing],
  );

  const moveDrag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!drag) return;
      const moved =
        drag.moved ||
        Math.hypot(
          event.clientX - startRef.current.x,
          event.clientY - startRef.current.y,
        ) > DRAG_THRESHOLD_PX;

      setDrag({ ...drag, x: event.clientX, y: event.clientY, moved });
      setHover(moved ? locate(event.clientX, event.clientY) : null);
    },
    [drag, locate],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!drag) return;

      if (drag.moved) {
        draggedRef.current = true;
        const target = locate(event.clientX, event.clientY);
        if (typeof target === 'number') onPlace(drag.flag, target);
        else if (target === 'tray') returnToTray(drag.flag);
        setSelected(null);
      }

      setDrag(null);
      setHover(null);
    },
    [drag, locate, onPlace, returnToTray],
  );

  /** Swallows the click that follows a drag, so it does not also open the preview. */
  const consumedByDrag = () => {
    if (!draggedRef.current) return false;
    draggedRef.current = false;
    return true;
  };

  /**
   * A pointer tap opens the preview. Keyboard activation must not, or there
   * would be no way to place a flag without a mouse — so Enter/Space selects
   * instead, and `detail === 0` marks the click React synthesises from a key.
   */
  const tapFlag = (event: React.MouseEvent, flag: number) => {
    if (consumedByDrag()) return;
    if (!isPlacing || event.detail === 0) return;
    setPreview(flag);
  };

  const keySelectFlag = (event: React.KeyboardEvent, flag: number) => {
    if (!isPlacing) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setSelected((previous) => (previous === flag ? null : flag));
  };

  const activateSlot = (slot: number, fromKeyboard: boolean) => {
    if (!isPlacing) return;
    if (selected !== null) {
      onPlace(selected, slot);
      setSelected(null);
      return;
    }
    const placed = placements[slot];
    if (placed === null) return;
    if (fromKeyboard) returnToTray(placed);
    else setPreview(placed);
  };

  const slotState = (slot: number) => {
    if (phase === 'resolved') return RIGHT;
    if (phase === 'revealing') return results?.[slot] ? RIGHT : WRONG;
    if (hover === slot) return HOVER;
    return EMPTY;
  };

  return (
    <>
      <div
        onClick={isResolved ? onAdvance : undefined}
        className={`flex w-full ${BOARD_HEIGHT} ${
          isResolved ? 'cursor-pointer' : ''
        }`}
      >
        <div className="flex flex-col w-full">
          {cities.map((city, slot) => {
            const placed = placements[slot];
            const trayFlag = trayOrder[slot];
            const trayFlagPlaced = placements.includes(trayFlag);

            return (
              <div
                key={city.id}
                /* min-h-0 so the rows share the board height instead of being
                   pushed taller by the flag images. */
                /* md:py-0.5 keeps the drop zone and the flag card off the row
                   edges — `max-h-full` otherwise lets them fill the row exactly
                   and adjacent rows' boxes end up touching on shorter screens. */
                className="flex flex-1 min-h-0 items-center gap-2 border-b border-gray-200 last:border-b-0 md:py-0.5 dark:border-white/10"
              >
                {/* City — left aligned; App.css centres text globally. */}
                <div className="flex-1 min-w-0 pl-[7px] text-left">
                  <p
                    className={`font-bold leading-tight text-base md:text-xl ${
                      config.labelWraps ? 'break-words' : 'truncate'
                    }`}
                  >
                    {city.name}
                  </p>
                  {city.country && (
                    <p className="truncate text-xs md:text-sm leading-tight text-gray-500 dark:text-slate-400">
                      {city.country}
                    </p>
                  )}
                </div>

                {/* Drop zone */}
                <button
                  type="button"
                  ref={(element) => {
                    slotRefs.current[slot] = element;
                  }}
                  aria-label={`${city.name}, ${city.country}`}
                  onClick={() => activateSlot(slot, false)}
                  onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.preventDefault();
                    activateSlot(slot, true);
                  }}
                  onPointerDown={(event) =>
                    placed !== null && startDrag(event, placed)
                  }
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                  className={[
                    BOX,
                    config.boxAspect,
                    'flex flex-col items-center justify-center gap-0.5',
                    'rounded-xl border-2 p-1 transition-colors touch-none',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1a76d2]',
                    placed !== null && phase === 'placing'
                      ? 'border-solid border-gray-200 bg-white shadow-sm dark:border-slate-600 dark:bg-slate-800 cursor-grab'
                      : slotState(slot),
                  ].join(' ')}
                >
                  {placed !== null ? (
                    <img
                      src={cities[placed].flag}
                      alt=""
                      draggable={false}
                      className={`max-w-full max-h-full object-contain pointer-events-none ${
                        drag?.flag === placed ? 'opacity-30' : ''
                      }`}
                    />
                  ) : (
                    <>
                      <LocationOnOutlinedIcon
                        className="text-gray-400 dark:text-slate-500"
                        style={{ fontSize: 18 }}
                      />
                      <span className="px-0.5 text-[9px] md:text-[11px] italic leading-tight text-gray-400 dark:text-slate-500">
                        {t(`${i18nBase}.drop-here`)}
                      </span>
                    </>
                  )}
                </button>

                {/* Flag tile for this row's position in the shuffled set */}
                <div
                  ref={(element) => {
                    trayRefs.current[slot] = element;
                  }}
                  className={`${BOX} ${config.boxAspect} flex items-center justify-center`}
                >
                  {!trayFlagPlaced && (
                    <button
                      type="button"
                      aria-label={`${t(`${i18nBase}.tray`)} ${slot + 1}`}
                      aria-pressed={selected === trayFlag}
                      onClick={(event) => tapFlag(event, trayFlag)}
                      onKeyDown={(event) => keySelectFlag(event, trayFlag)}
                      onPointerDown={(event) => startDrag(event, trayFlag)}
                      onPointerMove={moveDrag}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                      className={[
                        'flex flex-col w-full h-full items-center justify-center',
                        'rounded-xl bg-white dark:bg-slate-100 p-0.5 shadow-md',
                        'transition-shadow touch-none',
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1a76d2]',
                        isPlacing ? 'cursor-grab hover:shadow-lg' : '',
                        selected === trayFlag
                          ? 'ring-2 ring-[#1a76d2] ring-offset-1 dark:ring-offset-slate-900'
                          : '',
                      ].join(' ')}
                    >
                      <img
                        src={cities[trayFlag].flag}
                        alt=""
                        draggable={false}
                        className={`flex-1 min-h-0 max-w-full object-contain pointer-events-none ${
                          drag?.flag === trayFlag ? 'opacity-30' : ''
                        }`}
                      />
                      {/* Grip, as in the mockup — signals "this is draggable".
                          The card stays light in both themes, so this needs a
                          mid grey rather than a near-white tint to read. */}
                      <DragIndicatorIcon
                        className="shrink-0 rotate-90 text-gray-500"
                        style={{ fontSize: 11 }}
                      />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/*
          The tile under the finger. Fixed to the pointer rather than reparented,
          which keeps cross-container dragging free of layout maths.

          The transform is inline: this Tailwind config sets `important: true`,
          so a utility like `scale-105` would emit `transform: ... !important`
          and override it, pinning the ghost to the corner.
        */}
        {drag?.moved && (
          <div
            style={{
              position: 'fixed',
              left: drag.x,
              top: drag.y,
              width: drag.width,
              height: drag.height,
              transform: 'translate(-50%, -50%) scale(1.05)',
              pointerEvents: 'none',
              zIndex: 60,
            }}
            className="flex items-center justify-center rounded-xl border-2 border-[#1a76d2] bg-white p-1 shadow-2xl"
          >
            <img
              src={cities[drag.flag].flag}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          </div>
        )}
      </div>

      <ItemPreviewModal
        config={config}
        flag={preview}
        cities={cities}
        onClose={() => setPreview(null)}
      />
    </>
  );
};
